import { Box, Section, Text } from "@vinomofo/components";
import {
  CountdownTimer,
  calculateTimeLeft,
} from "../components/offer-card/CountdownTimer";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { DividerHeading } from "../components/divider-heading";
import { GatsbyImage } from "gatsby-plugin-image";
import { Layout } from "../components/layout";
import MQOffer from "../components/marqueeoffer/";
import { MofoSocialProofs } from "../components/mofo-social-proofs";
import { OfferCard } from "../components/offer-card/OfferCard";
import SEO from "../components/seo/seo";
import { VinoUser } from "../contexts/auth/state";
import config from "../../config.json";
import { generateStorefrontImage } from "../utils/generate-storefront-image";
import isEqual from "lodash.isequal";
import { isFlagEnabled } from "../utils/is-flag-enabled";
import { offerCardPropsFromVinoDataOffer } from "../transformers/vino-data/offer-card";
import { offerDetailsSinglePropsFromVinoDataOffer } from "../transformers/vino-data/offer-details-single";
import styled from "styled-components";
import useAuth from "../hooks/use-auth";
import { useLocation } from "@reach/router";
import vinoFetch from "../utils/vinoFetch";
import { determineTierIndex, isUserAStaff } from "../utils/loyalty";
import { useLoyalty } from "../hooks/account/use-loyalty";
import PayWall from "../components/pay-wall/PayWall";
import { ProductPreloader } from "../components/preloaders/ProductPreloader";
import { EventPageProps } from "../types/vino-data-types";

const StyledOfferGrid = styled(Box)<{
  columns: number;
  neaten: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${({ neaten }) => (!neaten ? "60px 10px" : "30px 8px")};

  ${({ neaten }) =>
    neaten && {
      paddingTop: "0.8em",
    }}

  @media ${({ theme }) => theme.mq.min.tablet} {
    grid-template-columns: repeat(
      ${({ columns = 3 }) => columns},
      minmax(23.8%, 1fr)
    );
    grid-gap: ${({ neaten }) => (!neaten ? "5em 1em" : "1em")};

    ${({ neaten }) =>
      neaten && {
        paddingTop: "0.8em",
      }}
  }
`;

const EventPage: React.FC<EventPageProps> = ({ pageContext }): JSX.Element => {
  const { user, isLoading: authLoading } = useAuth();
  const { loyalty, showLoyalty, isLoading } = useLoyalty();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const { eventDetails } = pageContext;
  const [events, setEvents] = useState(eventDetails);
  const isLoyaltyEvent =
    Array.isArray(events?.tiersEligible) &&
    events?.tiersEligible?.length > 0 &&
    !events?.tiersEligible?.some((tier: string) =>
      ["GUEST", "ALL"].includes(tier)
    );

  const [isEligible, setIsEligible] = useState(
    !events.tiersEligible || events.tiersEligible?.includes("ALL")
  );
  const [modal, setModal] = useState<any>(<ProductPreloader />);

  const timeLeft = useMemo(() => calculateTimeLeft(new Date(events.endsAt)), [
    events.endsAt,
  ]);

  const { tier } = determineTierIndex(
    user as VinoUser,
    showLoyalty,
    loyalty?.loyalty_tier
  );
  const customer_loyalty_tier = tier?.toUpperCase();
  const isExpired = events?.endsAt ? timeLeft === null : false;

  const rawOffer = events?.marqueeOffers?.[0]?.offer;
  const hasCountdown = events.endsAt && !events.hideCountdown;
  const isSocialProofEnabled =
    config[process.env.GATSBY_VM_MARKET as keyof typeof config]
      ?.ENABLE_SOCIAL_PROOF;
  const { eventOffers } = events;
  const tierMap = config[process.env.GATSBY_VM_MARKET]["TIER_MAP"] || {};

  const isFirstRowWithDisplayFlags = eventOffers
    ?.slice(0, 2)
    ?.some(
      (offer) =>
        offer?.offer?.displayFlags &&
        Object.keys(offer?.offer?.displayFlags).length > 0
    );

  const details = useMemo(
    () =>
      rawOffer ? offerDetailsSinglePropsFromVinoDataOffer(rawOffer) : null,
    [rawOffer]
  );

  const bannerImageLarge = useMemo(
    () => generateStorefrontImage(events.bannerImageLarge),
    [events.bannerImageLarge]
  );

  const bannerImageSmall = useMemo(
    () =>
      generateStorefrontImage(
        events.bannerImageSmall || events.bannerImageLarge
      ),
    [events.bannerImageSmall, events.bannerImageLarge]
  );

  const eligibleOffers = useMemo(() => {
    return eventOffers.filter((o) => {
      if (isUserAStaff(user as VinoUser)) {
        return true;
      }

      if (!user) {
        return (
          o.offer.tiersEligible?.includes("GUEST") ||
          o.offer.tiersEligible?.includes("ALL")
        );
      }

      if (showLoyalty && customer_loyalty_tier) {
        return (
          o.offer.tiersEligible?.includes(customer_loyalty_tier) ||
          o.offer.tiersEligible?.includes("ALL")
        );
      }

      return o.offer.tiersEligible?.includes("ALL");
    });
  }, [eventOffers, customer_loyalty_tier, user, showLoyalty]);

  const isCustomerEligible = useMemo(
    () =>
      Array.isArray(events.tiersEligible) &&
      events?.tiersEligible.some(
        (tier) =>
          tierMap[user?.customer_group?.name?.toUpperCase() || "GREEN"] >=
          tierMap[tier.toUpperCase()]
      ),
    [events, user]
  );

  useEffect(() => {
    const fetchRefreshEvent = async () => {
      try {
        const res = await vinoFetch.get(`/api/refresh-event?id=${events.id}`);
        if (!isEqual(events, res.data)) {
          setEvents(res.data);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error refreshing event data:", error);
        setError("Failed to load event data");
        setLoading(false);
      }
    };

    fetchRefreshEvent();
  }, [events.id]);

  const [divHeight, setDivHeight] = useState(0);
  const divRef = useRef(null);
  const updateDivHeight = useCallback(() => {
    if (divRef.current) {
      console.log("divRef.current ", divRef.current);
      setDivHeight(divRef.current.clientHeight);
    }
  }, []);

  // Use useLayoutEffect to add and clean up the resize event listener
  useLayoutEffect(() => {
    updateDivHeight();

    window.addEventListener("resize", updateDivHeight);

    return () => {
      window.removeEventListener("resize", updateDivHeight);
    };
  }, []);

  useEffect(() => {
    if (!events.tiersEligible || events.tiersEligible?.includes("ALL")) {
      setIsEligible(true);
      return;
    }
    if (authLoading) {
      return;
    }
    if (user && isUserAStaff(user)) {
      setIsEligible(true);
      return;
    }
    if (!user && !events.tiersEligible?.includes("GUEST")) {
      setModal(
        <PayWall
          title={"Login to see all of your exclusive VIM deals."}
          LoginButton={true}
          cause={"guest_cannot_see"}
          theme="modal"
          category="Event"
          page_tier={events.tiersEligible.toString()}
          user_tier={"GUEST"}
        />
      );
      setIsEligible(false);
      return;
    }
    if (!user && events.tiersEligible?.includes("GUEST")) {
      setIsEligible(true);
      return;
    }
    if (isLoading) {
      return;
    }
    if (!isCustomerEligible) {
      setModal(
        <PayWall
          title={
            "This is awkward... Earn more VIM points to unlock this event."
          }
          LoginButton={false}
          cause={"tier_too_low"}
          theme="full"
          category="Event"
          page_tier={events.tiersEligible.toString()}
          user_tier={user?.customer_group?.name?.toUpperCase()}
        />
      );
      setIsEligible(false);
      return;
    }
    setIsEligible(true);
  }, [user, authLoading, isCustomerEligible]);

  if (!isEligible) {
    return (
      <Layout>
        <SEO
          title={events.seoTitle || events.name}
          type={"event"}
          image={events.bannerImageSmall?.url || ""}
          pathname={events.route?.path || ""}
          description={events.seoDescription || ""}
          robots={isLoyaltyEvent ? "noindex, nofollow" : "index, follow"}
        />
        {modal}
      </Layout>
    );
  }
  const EventDetails = () => {
    return (
      <>
        <Box
          height="635px"
          position="relative"
          display={["none", "block", "block"]}
        >
          {rawOffer && (
            <MQOffer
              rawOffer={rawOffer}
              locationHref={location.href}
              details={details}
              isExpired={isExpired}
              endsAt={events.endsAt}
              pt={40}
            />
          )}
          <GatsbyImage
            image={bannerImageLarge}
            style={{
              width: "100%",
              height: "100%",
              textAlign: "center",
              position: "absolute",
              zIndex: -1,
              top: 0,
            }}
            imgStyle={{
              objectFit: "cover",
              objectPosition: "center top",
              top: "auto",
              bottom: "0",
              width: "100%",
              height: "100%",
              maxWidth: "2000px",
              margin: "0 auto",
              filter: isExpired ? "grayscale(100%)" : "none",
            }}
            alt=""
          />
        </Box>
        <Box
          height={rawOffer ? 400 : 300}
          position="relative"
          display={["block", "none", "none"]}
        >
          {rawOffer && (
            <Box ref={divRef}>
              <MQOffer
                rawOffer={rawOffer}
                locationHref={location.href}
                details={details}
                isExpired={isExpired}
                endsAt={events.endsAt}
                pt={40}
              />
            </Box>
          )}
          <GatsbyImage
            image={bannerImageSmall}
            style={{
              width: "100%",
              height: "100%",
              textAlign: "center",
              position: "absolute",
              zIndex: -1,
              top: 0,
            }}
            imgStyle={{
              objectFit: "cover",
              objectPosition: "center top",
              top: "auto",
              bottom: "0",
              width: "100%",
              height: "100%",
              maxWidth: "2000px",
              margin: "0 auto",
              filter: isExpired ? "grayscale(100%)" : "none",
            }}
            alt=""
          />
        </Box>
        {isSocialProofEnabled && eventOffers?.length > 0 && (
          <MofoSocialProofs
            eventEndTime={
              hasCountdown && events.endsAt
                ? new Date(events.endsAt)
                : undefined
            }
            eventStartTime={new Date(events.startsAt)}
            eventOffers={eligibleOffers}
          />
        )}
        {hasCountdown && !isSocialProofEnabled && (
          <Section
            mt={[
              rawOffer ? `${divHeight - 300}px` : 30,
              rawOffer ? 40 : 30,
              30,
            ]}
          >
            {isExpired ? (
              <DividerHeading label="Event Finished" />
            ) : (
              <CountdownTimer
                start={new Date(events.startsAt)}
                end={
                  new Date() <= new Date(events.startsAt)
                    ? new Date(events.startsAt)
                    : new Date(events.endsAt)
                }
                width="300px"
                height="70px"
                countDays={true}
                timeFontSize={"30px"}
                eventPage={true}
                spacing={"0 7px"}
                divider={true}
              />
            )}
          </Section>
        )}
        {new Date() >= new Date(events.startsAt) ? (
          <Section
            mt={[
              !isSocialProofEnabled && rawOffer
                ? 60
                : isFirstRowWithDisplayFlags
                ? 35
                : 15,
              !isSocialProofEnabled && rawOffer
                ? 60
                : isFirstRowWithDisplayFlags
                ? 45
                : 40,
              !isSocialProofEnabled && rawOffer
                ? 60
                : isFirstRowWithDisplayFlags
                ? 50
                : 30,
            ]}
          >
            <Box>
              <StyledOfferGrid mb={60} columns={4}>
                {events
                  ? eligibleOffers?.map((eventOffer, index) => {
                      return (
                        <OfferCard
                          key={`${eventOffer.offer.id}-${index}`}
                          {...offerCardPropsFromVinoDataOffer(eventOffer.offer)}
                          offerJson={eventOffer.offer}
                          skeleton={false}
                          height="100%"
                          variant="responsive"
                          eventPage={true}
                          showCountdown={isFlagEnabled(
                            eventOffer.offer.displayFlags,
                            "SHOW_COUNTDOWN"
                          )}
                          columns={4}
                        />
                      );
                    })
                  : null}
              </StyledOfferGrid>
            </Box>
          </Section>
        ) : (
          <Section my={50}>
            <Text fontSize={[6, 10]} variant="lg" textAlign={"center"}>
              Epic deals dropping soon!
            </Text>
          </Section>
        )}
      </>
    );
  };

  return (
    <Layout pathname={events.route?.path}>
      <SEO
        title={events.seoTitle || events.name}
        type={"event"}
        image={events.bannerImageSmall?.url || ""}
        pathname={events.route?.path || ""}
        description={events.seoDescription || ""}
        robots={
          isLoyaltyEvent && events?.noIndex
            ? "noindex, nofollow"
            : "index, follow"
        }
      />
      {isLoyaltyEvent ? (
        <>
          {loading ? (
            <Box minHeight="20vh">
              <ProductPreloader />
            </Box>
          ) : error ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={[4, 5]}
              px={[3, 4]}
            >
              <Text variant="sm" textAlign="center">
                {error}
              </Text>
            </Box>
          ) : (
            <EventDetails />
          )}
        </>
      ) : (
        <EventDetails />
      )}
    </Layout>
  );
};

export default EventPage;
